// 止め (アリスの技を手本にした版・DEV ?lunaExec=swap・src/luna_exec_swap.ts) の光。
//   すべて見た目の時計 (aliceClockMs) だけで決まる = 同じ時刻なら同じ絵 (撮影・比較で揺れない)。
//   色は冷たい銀 (#e8f0ff)・淡い青 (#a8c4ff)・少しだけ薄紫。白い効果線は描かない = 裂け目も火花の粒の列で描き、粒が散って落ちる。
//   流れ: 剣先の小さな光 → 駆け抜けた跡の銀の欠片 → 止めの間に裂け目が順に開く → 一点へ寄る → 弾ける → 銀の欠片が降る。
//   ?lunaExec=swap2 (作者の答え 10-19「点線に見える」「待ち時間が長い」「ハジケをもっと大きく」) = 裂け目を一続きの細い裂け目に・弾けを前倒しして大きく。
//   配布版 (lamp・10-20 作者の答え「ガラスっぽくていいんだけど、常夜との光が漏れているような感じに」) = 裂け目と弾けを
//     割れたガラスの裂け目から常夜燈の光が漏れる形 (src/luna_lamp_leak_fx.ts) に替える。剣先の光・駆け抜けの跡は同じ。
import { ALICE, STAR, lunaExecPierceOn, lunaExecStarOn, aliceBurstAtClock, aliceConvergeAtClock, aliceCutRevealMs, aliceCutStaggerMs, aliceFreezeMs, lunaExecLampOn, lunaExecSwap2On } from './luna_exec_swap'
import { drawGlassBurst, drawGlassSeam, lampFlicker } from './luna_lamp_leak_fx'

type Pt = { x: number; y: number }

const SILVER: readonly [number, number, number] = [232, 240, 255]
const PALE_BLUE: readonly [number, number, number] = [168, 196, 255]
const LAVENDER: readonly [number, number, number] = [200, 186, 255]

/** 0〜1 の決まった乱数 (i と種から)。 */
function rnd(i: number, seed: number): number {
  const s = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453
  return s - Math.floor(s)
}
const clamp01 = (u: number): number => Math.max(0, Math.min(1, u))
const rgba = (c: readonly [number, number, number], a: number): string => `rgba(${c[0]},${c[1]},${c[2]},${clamp01(a).toFixed(3)})`
function tint(i: number, seed: number): readonly [number, number, number] {
  const r = rnd(i, seed)
  return r < 0.55 ? SILVER : r < 0.88 ? PALE_BLUE : LAVENDER
}

function dot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, c: readonly [number, number, number], a: number): void {
  if (a <= 0.01 || r <= 0.05) return
  ctx.fillStyle = rgba(c, a)
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
}
function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, c: readonly [number, number, number], a: number): void {
  if (a <= 0.01 || r <= 0.5) return
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, rgba(c, a)); g.addColorStop(0.35, rgba(c, a * 0.35)); g.addColorStop(1, rgba(c, 0))
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
}

/** 撃破の時計 (finisher.elapsedMs) の ms を、見た目の時計へ。 */
export const aliceClockOfFinisher = (finisherMs: number): number => ALICE.hitMs + aliceFreezeMs() + finisherMs
const convergeAt = (): number => aliceClockOfFinisher(aliceConvergeAtClock())
const burstAt = (): number => aliceClockOfFinisher(aliceBurstAtClock())

/** 裂け目 i の角度 (ラジアン)。手本の星形 = 向きを散らし、近い角度が並ばないようにする。 */
export function aliceCutAngle(i: number): number {
  const base = (i * 0.618034) % 1   // 黄金比で散らす
  return (base * Math.PI) + (rnd(i, 3) - 0.5) * 0.22
}
export function aliceCutLength(i: number, scale: number): number { return (130 + rnd(i, 5) * 90) * scale }

/** 配布版の裂け目が開ききった時の縦の広がり (中心からの上端・下端・倍率 scale・縮めない時)。伏せた体に乗せる計算に使う (10-21)。 */
export function aliceLampSeamExtentY(scale: number): { top: number, bottom: number } {
  let top = 0, bottom = 0
  for (let i = 0; i < ALICE.cutCount; i++) {
    const oy = ((i + rnd(i, 9)) / ALICE.cutCount - 0.5) * 110 * scale
    const half = Math.abs(Math.sin(aliceCutAngle(i))) * aliceCutLength(i, scale) * 0.72 / 2
    top = Math.min(top, oy - half); bottom = Math.max(bottom, oy + half)
  }
  return { top, bottom }
}

export interface AliceFxInput {
  clockMs: number
  /** 主人公の足元 (駆け抜けのずれを足す前)。 */
  start: Pt
  /** 駆け抜けた先の足元。 */
  end: Pt
  facing: 1 | -1
  /** 裂け目の中心 (ボスの胴)。 */
  center: Pt
  /** ボスの大きさに合わせた倍率 (人の背丈 = 1)。 */
  scale: number
  /** 床の高さ (砕けたガラスが落ちて止まる所)。指定なし = end.y。 */
  floorY?: number
  /** 配布版の裂け目の並びを縦にだけ縮める倍率 (10-21・伏せた体に乗せる)。指定なし = 1 (縮めない)。 */
  seamSquashY?: number
}

/** 世界の層 (カメラの平行移動の内側) に描く。 */
export function drawLunaAliceFx(ctx: CanvasRenderingContext2D, f: AliceFxInput): void {
  const t = f.clockMs
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  drawGlint(ctx, f, t)
  // 一突きの版 (DEV ?lunaExec=pierce): 駆け抜けの欠片・裂け目・弾けは出さない (軌跡と光は src/luna_pierce_fx.ts)
  if (lunaExecPierceOn()) { ctx.restore(); return }
  drawDashFlakes(ctx, f, t)
  if (lunaExecStarOn()) { drawStarFinisher(ctx, f, t); ctx.restore(); return }
  drawCuts(ctx, f, t)
  drawBurst(ctx, f, t)
  ctx.restore()
}

// 溜めの終わりに剣先 (低く後ろへ引いた切っ先) で一度だけ小さく光る。
function drawGlint(ctx: CanvasRenderingContext2D, f: AliceFxInput, t: number): void {
  const d = t - ALICE.glintMs
  if (d < -40 || d > 140) return
  const a = d < 0 ? 1 + d / 40 : Math.pow(1 - d / 140, 1.6)
  const x = f.start.x - f.facing * 46, y = f.start.y - 22
  glow(ctx, x, y, 16, SILVER, 0.55 * a)
  dot(ctx, x, y, 2.2, SILVER, a)
  for (let i = 0; i < 5; i++) {
    const ang = rnd(i, 11) * Math.PI * 2, dist = 3 + clamp01(d / 140) * (8 + rnd(i, 12) * 10)
    dot(ctx, x + Math.cos(ang) * dist, y + Math.sin(ang) * dist, 1.1, tint(i, 13), a * 0.8)
  }
}

// 駆け抜けた跡: 通った所に銀の欠片が残り、わずかに漂って消える (線にしない)。
function drawDashFlakes(ctx: CanvasRenderingContext2D, f: AliceFxInput, t: number): void {
  const N = 26, LIFE = 300
  for (let i = 0; i < N; i++) {
    const u = i / (N - 1)
    const born = ALICE.dashStartMs + u * (ALICE.dashEndMs - ALICE.dashStartMs)
    const age = t - born
    if (age < 0 || age > LIFE) continue
    const k = age / LIFE
    const x = f.start.x + (f.end.x - f.start.x) * u + (rnd(i, 21) - 0.5) * 14 + f.facing * -k * 10
    const y = f.start.y - 34 - rnd(i, 22) * 52 + k * k * 18
    dot(ctx, x, y, 0.9 + rnd(i, 23) * 1.3, tint(i, 24), (1 - k) * (0.5 + rnd(i, 25) * 0.5))
  }
}

// 裂け目: 当たった瞬間から止めの間に1本ずつ、端から端へ粒が灯る。止めが明けても宙に掛かり、粒をこぼす。
//   撃破の時計で一点へ寄って小さくなり、弾ける拍で消える。
function drawCuts(ctx: CanvasRenderingContext2D, f: AliceFxInput, t: number): void {
  const BURST_AT = burstAt(), CONVERGE_AT = convergeAt()
  if (t < ALICE.hitMs || t >= BURST_AT) return
  const conv = clamp01((t - CONVERGE_AT) / Math.max(1, BURST_AT - CONVERGE_AT))
  const pull = conv * conv * conv   // 寄りは最後に速い
  if (lunaExecLampOn()) { drawLampSeams(ctx, f, t, conv, pull); return }
  if (lunaExecSwap2On()) { drawSeams(ctx, f, t, conv, pull); return }
  for (let i = 0; i < ALICE.cutCount; i++) {
    const open = ALICE.hitMs + i * aliceCutStaggerMs()
    if (t < open) continue
    const reveal = clamp01((t - open) / aliceCutRevealMs())
    const ang = aliceCutAngle(i), len = aliceCutLength(i, f.scale)
    const ux = Math.cos(ang), uy = Math.sin(ang)
    const dir = rnd(i, 7) < 0.5 ? 1 : -1   // どちらの端から開くか
    const count = Math.round(len / 5)
    // 中点を体の上に散らす (同じ一点から放射させると星形に見える)。体の幅・背丈のおよそ全体を使う。
    const ox = (rnd(i, 8) - 0.5) * 70 * f.scale, oy = ((i + rnd(i, 9)) / ALICE.cutCount - 0.5) * 110 * f.scale
    const hang = clamp01((t - open - aliceCutRevealMs()) / 400)   // 開ききってからの時間 (薄れと落ちる粒)
    for (let j = 0; j < count; j++) {
      const s = j / (count - 1)
      const along = dir > 0 ? s : 1 - s
      if (along > reveal) continue
      if (rnd(i * 97 + j, 31) < 0.22) continue   // 途切れ (線に見せない)
      const off = (s - 0.5) * len * (1 - pull)
      const jit = (rnd(i * 97 + j, 32) - 0.5) * 3.2
      const x = f.center.x + ox * (1 - pull) + ux * off - uy * jit
      const y = f.center.y + oy * (1 - pull) + uy * off + ux * jit
      // 中ほどが明るく、端は細る
      const mid = 1 - Math.abs(s - 0.5) * 2
      const fresh = clamp01(1 - (reveal - along) * 3)   // 灯った直後は強く
      const a = (0.35 + 0.45 * mid + 0.4 * fresh) * (1 - 0.35 * hang) * (1 - 0.3 * pull)
      const r = (0.9 + 1.3 * mid * rnd(i * 97 + j, 33) + 0.8 * fresh) * (1 - 0.5 * pull)
      dot(ctx, x, y, r, tint(i * 97 + j, 34), a)
    }
    // 開く頭の小さな光
    if (reveal < 1) {
      const off = ((dir > 0 ? reveal : 1 - reveal) - 0.5) * len
      glow(ctx, f.center.x + ox + ux * off, f.center.y + oy + uy * off, 12, SILVER, 0.6)
    }
    // こぼれ落ちる粒 (開ききってから・寄り始めるまで)
    const shedStart = open + aliceCutRevealMs()
    for (let k = 0; k < 7; k++) {
      const born = shedStart + rnd(i * 13 + k, 41) * 360
      const age = t - born
      if (age < 0 || age > 520) continue
      const q = age / 520
      const s = rnd(i * 13 + k, 42) - 0.5
      const x0 = f.center.x + ox + ux * s * len, y0 = f.center.y + oy + uy * s * len
      const vx = (rnd(i * 13 + k, 43) - 0.5) * 40
      dot(ctx, x0 + vx * q, y0 + 90 * q * q + 8 * q, 1.0 + rnd(i * 13 + k, 44), tint(i * 13 + k, 45), (1 - q) * 0.8 * (1 - pull))
    }
  }
  // 寄った先の芯 (弾ける直前に強まる)
  if (conv > 0) glow(ctx, f.center.x, f.center.y, (10 + 20 * pull) * f.scale, SILVER, 0.25 + 0.55 * pull)
}

// swap2 の裂け目: 粒の列をやめ、一続きの細い裂け目 (中ほどが太く両端が尖る・縁がわずかにぎざつく) を面で塗る。
//   色は冷たい銀と淡い青 (白い効果線にしない)。開く頭が光り、縁から火花がこぼれる。寄りで細って一点へ集まる。
function seamOutline(f: AliceFxInput, i: number, from: number, to: number, halfW: number, shrink: number): Pt[] {
  const ang = aliceCutAngle(i), len = aliceCutLength(i, f.scale)
  const ux = Math.cos(ang), uy = Math.sin(ang)
  const ox = (rnd(i, 8) - 0.5) * 70 * f.scale * shrink, oy = ((i + rnd(i, 9)) / ALICE.cutCount - 0.5) * 110 * f.scale * shrink
  const N = 22
  const top: Pt[] = [], bot: Pt[] = []
  for (let j = 0; j <= N; j++) {
    const s = from + (to - from) * (j / N)
    const off = (s - 0.5) * len * shrink
    const taper = Math.pow(Math.sin(Math.PI * s), 0.85)
    const jag = (rnd(i * 61 + Math.round(s * 40), 36) - 0.5) * 1.6 * f.scale   // 縁のぎざつき (裂けた手触り)
    const w = halfW * taper
    const cx = f.center.x + ox + ux * off, cy = f.center.y + oy + uy * off
    top.push({ x: cx - uy * (w + jag), y: cy + ux * (w + jag) })
    bot.push({ x: cx + uy * (w - jag * 0.6), y: cy - ux * (w - jag * 0.6) })
  }
  return top.concat(bot.reverse())
}
function fillPoly(ctx: CanvasRenderingContext2D, pts: Pt[], style: string): void {
  if (pts.length < 3) return
  ctx.fillStyle = style
  ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y)
  for (let k = 1; k < pts.length; k++) ctx.lineTo(pts[k].x, pts[k].y)
  ctx.closePath(); ctx.fill()
}
function drawSeams(ctx: CanvasRenderingContext2D, f: AliceFxInput, t: number, conv: number, pull: number): void {
  const shrink = 1 - pull
  for (let i = 0; i < ALICE.cutCount; i++) {
    const open = ALICE.hitMs + i * aliceCutStaggerMs()
    if (t < open) continue
    const reveal = clamp01((t - open) / aliceCutRevealMs())
    const dir = rnd(i, 7) < 0.5 ? 1 : -1
    const from = dir > 0 ? 0 : 1 - reveal, to = dir > 0 ? reveal : 1
    const hang = clamp01((t - open - aliceCutRevealMs()) / 400)
    const fade = (1 - 0.3 * hang) * (1 - 0.25 * pull)
    const w = (4.2 + rnd(i, 35) * 1.8) * f.scale * (1 - 0.55 * pull)
    // 光る線にしない (撮って見たら白い効果線に見えた) = 空間が割れた暗い口 + 縁の淡い青の滲み。
    //   滲み (足し算の光) → 暗い芯 (普通の塗り) → 芯の縁だけ薄紫。
    fillPoly(ctx, seamOutline(f, i, from, to, w * 2.4, shrink), rgba(PALE_BLUE, 0.2 * fade))
    ctx.save(); ctx.globalCompositeOperation = 'source-over'
    fillPoly(ctx, seamOutline(f, i, from, to, w, shrink), `rgba(16,10,34,${(0.88 * fade).toFixed(3)})`)
    ctx.restore()
    fillPoly(ctx, seamOutline(f, i, from, to, w * 0.3, shrink), rgba(LAVENDER, 0.28 * fade))
    const ang = aliceCutAngle(i), len = aliceCutLength(i, f.scale)
    const ux = Math.cos(ang), uy = Math.sin(ang)
    const ox = (rnd(i, 8) - 0.5) * 70 * f.scale * shrink, oy = ((i + rnd(i, 9)) / ALICE.cutCount - 0.5) * 110 * f.scale * shrink
    // 開く頭の光
    if (reveal < 1) {
      const off = ((dir > 0 ? reveal : 1 - reveal) - 0.5) * len * shrink
      glow(ctx, f.center.x + ox + ux * off, f.center.y + oy + uy * off, 16 * f.scale, SILVER, 0.7)
    }
    // 縁からこぼれる火花 (裂け目と直角に弾かれ、重さで落ちる)
    for (let k = 0; k < 12; k++) {
      const sAt = rnd(i * 13 + k, 42)
      const along = dir > 0 ? sAt : 1 - sAt
      const born = open + along * aliceCutRevealMs() + rnd(i * 13 + k, 41) * 260
      const age = t - born
      if (age < 0 || age > 460) continue
      const q = age / 460
      const side = rnd(i * 13 + k, 46) < 0.5 ? 1 : -1
      const x0 = f.center.x + ox + ux * (sAt - 0.5) * len * shrink, y0 = f.center.y + oy + uy * (sAt - 0.5) * len * shrink
      const sp = (18 + rnd(i * 13 + k, 43) * 30) * f.scale * (1 - Math.pow(1 - q, 2))
      const x = x0 - uy * side * sp, y = y0 + ux * side * sp + 70 * q * q
      dot(ctx, x, y, (1.0 + rnd(i * 13 + k, 44) * 1.2) * (1 - 0.5 * q), tint(i * 13 + k, 45), (1 - q) * 0.9 * (1 - pull))
    }
  }
  if (conv > 0) glow(ctx, f.center.x, f.center.y, (14 + 30 * pull) * f.scale, SILVER, 0.3 + 0.6 * pull)
}

// 配布版 (lamp) の裂け目: 割れたガラスの裂け目が1本ずつ開き、奥から常夜燈の光が漏れる。止めの間は常夜燈と同じ揺らぎ。
//   寄りで細って一点へ集まる (前の版と同じ時刻)。
function drawLampSeams(ctx: CanvasRenderingContext2D, f: AliceFxInput, t: number, conv: number, pull: number): void {
  const shrink = 1 - pull
  const seams: { cx: number, cy: number, ang: number, len: number }[] = []
  const sy = f.seamSquashY ?? 1
  for (let i = 0; i < ALICE.cutCount; i++) {
    const open = ALICE.hitMs + i * aliceCutStaggerMs()
    if (t < open) continue
    const reveal = clamp01((t - open) / aliceCutRevealMs())
    const fresh = clamp01(1 - (t - open - aliceCutRevealMs()) / 120)   // 開いた直後は強く
    const ox = (rnd(i, 8) - 0.5) * 70 * f.scale * shrink, oy = ((i + rnd(i, 9)) / ALICE.cutCount - 0.5) * 110 * f.scale * shrink * sy
    const dx = Math.cos(aliceCutAngle(i)), dy = Math.sin(aliceCutAngle(i)) * sy
    const ang = Math.atan2(dy, dx), len = aliceCutLength(i, f.scale) * shrink * 0.72 * Math.hypot(dx, dy)
    const light = (0.72 + 0.28 * fresh) * lampFlicker(t / 1000, i) * (1 - 0.2 * pull)
    if (import.meta.env.DEV) seams.push({ cx: f.center.x + ox, cy: f.center.y + oy, ang, len })
    drawGlassSeam(ctx, { cx: f.center.x + ox, cy: f.center.y + oy, ang, len,
      reveal, fromEnd: rnd(i, 7) < 0.5 ? 1 : -1, light, scale: f.scale * (1 - 0.45 * pull), seed: i * 31 + 5, head: true })
  }
  // DEV 計測フック (10-21・scripts/probe_exec_crack_position.mjs): いま描いた裂け目の中点・向き・長さ。
  if (import.meta.env.DEV) (globalThis as { __aliceSeams?: unknown }).__aliceSeams = { clock: t, seams }
  if (conv > 0) glow(ctx, f.center.x, f.center.y, (16 + 34 * pull) * f.scale, [170, 205, 255], 0.3 + 0.6 * pull)
}

// 弾ける: 小さな冷たい光が開き、火花が輪に散り、そのあと銀の欠片がゆっくり降る。
function drawBurst(ctx: CanvasRenderingContext2D, f: AliceFxInput, t: number): void {
  const d = t - burstAt()
  if (lunaExecLampOn()) { drawGlassBurst(ctx, { cx: f.center.x, cy: f.center.y, ms: d, scale: f.scale, floorY: f.floorY ?? f.end.y }); return }
  if (d < 0 || d > 2400) return
  const c = f.center
  // swap2 = 作者「ハジケをもっと大きく」: 光の広がり・火花の数と飛ぶ距離・欠片の数と散る幅を大きくする。
  const big = lunaExecSwap2On()
  const R = big ? 1.9 : 1
  // 開く光 (白で画面を隠さない大きさ)
  const GLOW_MS = big ? 380 : 320
  if (d < GLOW_MS) {
    const k = d / GLOW_MS
    glow(ctx, c.x, c.y, (40 + 80 * (1 - Math.pow(1 - k, 3))) * R * f.scale, PALE_BLUE, 0.7 * Math.pow(1 - k, 1.5))
    glow(ctx, c.x, c.y, 26 * (big ? 1.5 : 1) * f.scale, SILVER, 0.9 * Math.pow(1 - k, 2.2))
    // swap2: 体を包む広い薄紫の光をもう1枚 (白で画面を隠さない濃さ)
    if (big) glow(ctx, c.x, c.y, (90 + 120 * (1 - Math.pow(1 - k, 2))) * f.scale, LAVENDER, 0.55 * Math.pow(1 - k, 1.3))
  }
  // 火花の輪 (速く出て減速・重さで少し落ちる)
  const SPARKS = big ? 72 : 34
  for (let i = 0; i < SPARKS; i++) {
    const life = (300 + rnd(i, 51) * 260) * (big ? 1.3 : 1)
    if (d > life) continue
    const q = d / life
    const ang = (i / SPARKS) * Math.PI * 2 + (rnd(i, 52) - 0.5) * 0.35
    const v = (120 + rnd(i, 53) * 140) * f.scale * (big ? 1.7 : 1)
    const dist = v * (1 - Math.pow(1 - q, 3)) * 0.9
    dot(ctx, c.x + Math.cos(ang) * dist, c.y + Math.sin(ang) * dist + 30 * q * q * (big ? 1.8 : 1),
      (1.2 + rnd(i, 54) * 1.4) * (big ? 1.35 : 1) * (1 - 0.6 * q), tint(i, 55), Math.pow(1 - q, 1.3))
  }
  // 降る銀の欠片 (花びらの代わり。揺れながら落ちる)
  const FLAKES = big ? 80 : 40
  for (let i = 0; i < FLAKES; i++) {
    const born = 60 + rnd(i, 61) * 260
    const age = d - born
    const life = 1300 + rnd(i, 62) * 600
    if (age < 0 || age > life) continue
    const q = age / life
    const spread = (rnd(i, 63) - 0.5) * 220 * f.scale * (big ? 1.8 : 1)
    const rise = (20 + rnd(i, 64) * 50) * f.scale * (big ? 1.6 : 1)
    const x = c.x + spread * (1 - Math.pow(1 - Math.min(1, q * 3), 2)) + Math.sin(age / 210 + i) * 7
    const y = c.y - rise * Math.min(1, q * 4) + q * q * 150 + q * 40
    const a = (q < 0.1 ? q / 0.1 : 1) * Math.pow(1 - q, 1.2) * (0.5 + rnd(i, 65) * 0.4)
    const w = 1.2 + rnd(i, 66) * 1.6
    ctx.fillStyle = rgba(tint(i, 67), a)
    ctx.save(); ctx.translate(x, y); ctx.rotate(age / (180 + rnd(i, 68) * 200) + i)
    ctx.fillRect(-w, -w * 0.45, w * 2, w * 0.9)
    ctx.restore()
  }
}

// 星型の版 (DEV ?lunaExec=star・10-30 作者の答え)。裂け目の光とガラスは出さない。
//   ①当たりの止めの間に、斬った細い線が体を貫いてパパパッと走って消える ②止めが明けると線が星型 (五芒星) に結ぶ
//   ③星はゆっくりの間掛かったまま ④重い一撃で星が一度強く光って消え、ボスの体から光が溢れて (放射の筋と光の粒) 静かに引く。
function line(ctx: CanvasRenderingContext2D, a: Pt, b: Pt, w: number, c: readonly [number, number, number], al: number): void {
  if (al <= 0.01) return
  ctx.strokeStyle = rgba(c, al); ctx.lineWidth = w; ctx.lineCap = 'round'
  ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke()
}
function slashLine(ctx: CanvasRenderingContext2D, a: Pt, b: Pt, al: number, glowK = 1): void {
  line(ctx, a, b, 9 * glowK, PALE_BLUE, 0.18 * al)
  line(ctx, a, b, 3.2 * glowK, PALE_BLUE, 0.5 * al)
  line(ctx, a, b, 1.3, SILVER, al)
}
export function starPoints(cx: number, cy: number, r: number): Pt[] {
  // 五芒星を描く順の頂点 (一筆書き)。上の頂点から2つ飛ばし。
  const pts: Pt[] = []
  for (let k = 0; k <= 5; k++) {
    const a = -Math.PI / 2 + (k * 2 % 5) * (Math.PI * 2 / 5)
    pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r })
  }
  return pts
}
function drawStarFinisher(ctx: CanvasRenderingContext2D, f: AliceFxInput, t: number): void {
  const c = f.center
  // ① 斬った線
  for (let i = 0; i < STAR.cutCount; i++) {
    const born = ALICE.hitMs + i * STAR.cutStaggerMs
    const age = t - born
    if (age < 0 || age > STAR.cutLifeMs) continue
    const ang = aliceCutAngle(i), len = aliceCutLength(i, f.scale) * 1.5
    const ox = (rnd(i, 31) - 0.5) * 40 * f.scale, oy = (rnd(i, 32) - 0.5) * 60 * f.scale
    const ux = Math.cos(ang), uy = Math.sin(ang), dir = rnd(i, 7) < 0.5 ? 1 : -1
    const grow = clamp01(age / 22)
    const a = { x: c.x + ox - dir * ux * len / 2, y: c.y + oy - dir * uy * len / 2 }
    const b = { x: a.x + dir * ux * len * grow, y: a.y + dir * uy * len * grow }
    // 走り終えたら尾から細って消える
    const tail = clamp01((age - 22) / (STAR.cutLifeMs - 22))
    const a2 = { x: a.x + (b.x - a.x) * tail, y: a.y + (b.y - a.y) * tail }
    slashLine(ctx, a2, b, age < 22 ? 1 : 1 - tail * 0.6)
  }
  // ②③ 星型
  const fin0 = aliceClockOfFinisher(0)
  const starAt = fin0 + STAR.starFromClock, heavy = fin0 + STAR.heavyAtClock
  const r = 95 * f.scale
  const pts = starPoints(c.x, c.y, r)
  if (t >= starAt && t < heavy + 220) {
    const flash = t >= heavy ? 1 - clamp01((t - heavy) / 220) : 0
    const spread = t >= heavy ? 1 + clamp01((t - heavy) / 220) * 0.35 : 1
    const al = t >= heavy ? flash : 0.75 + 0.25 * lampFlicker(t / 1000, 3)
    for (let k = 0; k < 5; k++) {
      const e = clamp01((t - starAt - k * STAR.starEdgeMs) / STAR.starEdgeMs)
      if (e <= 0) break
      const a = pts[k], b = pts[k + 1]
      const sa = { x: c.x + (a.x - c.x) * spread, y: c.y + (a.y - c.y) * spread }
      const sb = { x: c.x + (b.x - c.x) * spread, y: c.y + (b.y - c.y) * spread }
      slashLine(ctx, sa, { x: sa.x + (sb.x - sa.x) * e, y: sa.y + (sb.y - sa.y) * e }, al, 1 + flash * 1.6)
    }
  }
  // ④ 重い一撃 → ボスから光が溢れる
  const d = t - heavy
  if (d < 0 || d > STAR.spillMs) return
  const k = d / STAR.spillMs
  const rise = clamp01(d / 120), fade = Math.pow(1 - k, 1.4)
  glow(ctx, c.x, c.y, (60 + 160 * rise) * f.scale, SILVER, 0.75 * fade * (d < 60 ? 1.3 : 1))
  glow(ctx, c.x, c.y, (140 + 220 * k) * f.scale, PALE_BLUE, 0.35 * fade)
  const N = 12
  for (let i = 0; i < N; i++) {
    const ang = (i / N) * Math.PI * 2 + (rnd(i, 41) - 0.5) * 0.4 + k * 0.15
    const len = (90 + rnd(i, 42) * 150) * f.scale * (0.4 + 0.6 * rise) * (1 + k * 0.5)
    const w = (6 + rnd(i, 43) * 8) * f.scale * (1 - k * 0.5)
    const ux = Math.cos(ang), uy = Math.sin(ang)
    const g = ctx.createLinearGradient(c.x, c.y, c.x + ux * len, c.y + uy * len)
    g.addColorStop(0, rgba(SILVER, 0.55 * fade)); g.addColorStop(1, rgba(PALE_BLUE, 0))
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.moveTo(c.x - uy * w, c.y + ux * w); ctx.lineTo(c.x + ux * len, c.y + uy * len); ctx.lineTo(c.x + uy * w, c.y - ux * w)
    ctx.closePath(); ctx.fill()
  }
  // 体から零れて立ちのぼる光の粒
  for (let i = 0; i < 40; i++) {
    const b0 = rnd(i, 51) * STAR.spillMs * 0.6
    const age = d - b0
    if (age < 0) continue
    const u = age / (STAR.spillMs - b0)
    if (u > 1) continue
    const x = c.x + (rnd(i, 52) - 0.5) * 120 * f.scale + Math.sin(age / 180 + i) * 6
    const y = c.y + (rnd(i, 53) - 0.3) * 120 * f.scale - u * (60 + rnd(i, 54) * 90)
    dot(ctx, x, y, 1 + rnd(i, 55) * 1.6, tint(i, 56), (1 - u) * 0.9)
  }
}
